import { Link } from 'gatsby';
import { rgba } from 'polished';
import React, { useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import styled from 'styled-components';
import { Logo, Text, Flex } from '../../../components/blocks';
import { useMedia } from '../../../utils/useMedia';

const Root = styled.footer`
  width: 100%;
  padding: ${({ theme }) => theme.spaces.l};
  background: ${({ theme }) => theme.backgroundColors.black};
  margin-top: auto;

  ${({ theme }) =>
    theme.screen.sp({
      paddingBottom: theme.spaces.ex,
    })}
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: ${({ theme }) => theme.windowSizes.m};
  margin: ${({ theme }) => `${theme.spaces.xl} auto ${theme.spaces.xxxl}`};
  gap: ${({ theme }) => theme.spaces.l};

  ${({ theme }) =>
    theme.screen.sp({
      flexDirection: 'column',
      gap: theme.spaces.xl,
    })}
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  list-style-type: none;
  margin: 0;
  padding: 0;

  ${({ theme }) =>
    theme.screen.sp({
      padding: `${theme.spaces.m} 0`,
    })}
`;

const Items = styled.li`
  margin: 0;
  padding: ${({ theme }) => theme.spaces.m} 0;

  ${({ theme }) =>
    theme.screen.sp({
      padding: 0,
    })}
`;

const Links = styled(Link)`
  display: inline-block;
  color: ${({ theme }) => theme.fontColors.white};
  padding: ${({ theme }) => theme.spaces.xs} 0;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  transition-duration: ${({ theme }) => theme.transitions.normal};

  &:hover {
    box-shadow: 0 1px 0 ${({ theme }) => rgba(theme.borderColors.white, 0.5)};
    opacity: 0.8;

    ${({ theme }) =>
      theme.screen.sp({
        boxShadow: 'none',
        backgroundColor: rgba(theme.backgroundColors.white, 0.2),
      })}
  }

  ${({ theme }) =>
    theme.screen.sp({
      width: '100%',
      padding: `${theme.spaces.l} 0`,
    })}
`;

const ExternalLinks = styled.a`
  display: inline-block;
  color: ${({ theme }) => theme.fontColors.white};
  padding: ${({ theme }) => theme.spaces.xs} 0;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  transition-duration: ${({ theme }) => theme.transitions.normal};

  &:hover {
    box-shadow: 0 1px 0 ${({ theme }) => rgba(theme.borderColors.white, 0.5)};
    opacity: 0.8;

    ${({ theme }) =>
      theme.screen.sp({
        boxShadow: 'none',
        backgroundColor: rgba(theme.backgroundColors.white, 0.2),
      })}
  }

  ${({ theme }) =>
    theme.screen.sp({
      width: '100%',
      padding: `${theme.spaces.l} 0`,
    })}
`;

const LogoWrap = styled(Link)`
  margin-top: ${({ theme }) => theme.spaces.l};
`;

const Copyright = styled.address`
  margin-bottom: ${({ theme }) => theme.spaces.l};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.fontColors.white};
  font-style: normal;
  text-align: center;
`;

type ArrowProps = {
  expand?: boolean;
};

const Arrow = styled.div<ArrowProps>`
  display: block;
  width: 8px;
  height: 8px;
  border: 1px solid transparent;
  border-top-color: ${({ theme }) => theme.backgroundColors.white};
  border-left-color: ${({ theme }) => theme.backgroundColors.white};
  transition: ${({ theme }) => theme.transitions.normal};
  transform: ${({ expand }) => (expand ? 'rotate(45deg)' : 'rotate(225deg)')};
`;

const OverrideAccordion = styled(Accordion)<ArrowProps>`
  [hidden] {
    display: none;
  }

  .accordion__button {
    ${Flex} {
      padding: ${({ theme }) => theme.spaces.m} 0;
    }
  }

  .accordion__panel {
    padding: ${({ theme }) => theme.spaces.l};
  }
`;

export const Footer: React.FC = () => {
  const [isAbout, setIsAbout] = useState(false);
  const [isFunction, setIsFunction] = useState(false);
  const [isProducts, setIsProducts] = useState(false);

  const expandIsAbout = () => {
    setIsAbout(!isAbout);
  };
  const expandIsFunction = () => {
    setIsFunction(!isFunction);
  };
  const expandIsProducts = () => {
    setIsProducts(!isProducts);
  };

  return (
    <Root>
      <Wrapper>
        <LogoWrap to="/">
          <Logo color="white" width="100px" />
        </LogoWrap>
        {useMedia(`(max-width: 799px)`) ? (
          <OverrideAccordion
            allowMultipleExpanded={true}
            allowZeroExpanded={true}
          >
            <AccordionItem>
              <List>
                <AccordionItemHeading onClick={expandIsAbout}>
                  <AccordionItemButton>
                    <Items>
                      <Flex justifyContent="space-between">
                        <Text size="xs" fontWeight="bold" color="white">
                          Dentisとは
                        </Text>
                        <Arrow expand={isAbout} />
                      </Flex>
                    </Items>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <Items>
                    <Links to="/features">Dentisの特長</Links>
                  </Items>
                  <Items>
                    <Links to="/benefits">歯科DXの業務</Links>
                  </Items>
                  <Items>
                    <Links to="/function">機能一覧</Links>
                  </Items>
                  <Items>
                    <Links to="/security">セキュリティの取り組み</Links>
                  </Items>
                  <Items>
                    <Links to="/support">充実のサポート</Links>
                  </Items>
                  <Items>
                    <Links to="/vision">Dentisの想い</Links>
                  </Items>
                </AccordionItemPanel>
              </List>
            </AccordionItem>
            <AccordionItem>
              <List>
                <AccordionItemHeading onClick={expandIsFunction}>
                  <AccordionItemButton>
                    <Items>
                      <Flex justifyContent="space-between">
                        <Text size="xs" fontWeight="bold" color="white">
                          便利な機能
                        </Text>
                        <Arrow expand={isFunction} />
                      </Flex>
                    </Items>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <Items>
                    <Links to="/kakaritsuke">予約システム</Links>
                  </Items>
                  <Items>
                    <Links to="/online">オンライン診療</Links>
                  </Items>
                  <Items>
                    <Links to="/questionnaire">Web問診</Links>
                  </Items>
                  <Items>
                    <Links to="/message">メッセージ配信</Links>
                  </Items>
                  <Items>
                    <Links to="/visit">訪問歯科</Links>
                  </Items>
                  <Items>
                    <Links to="/subkarte">サブカルテ</Links>
                  </Items>
                  <Items>
                    <Links to="/karte">カルテ・レセコン</Links>
                  </Items>
                  <Items>
                    <Links to="/analytics">分析・物販</Links>
                  </Items>
                </AccordionItemPanel>
              </List>
            </AccordionItem>
            <List>
              <Items>
                <Links to="/case">導入事例</Links>
              </Items>
              <Items>
                <Links to="/plan">料金</Links>
              </Items>
              <Items>
                <Links to="/news">お知らせ・イベント</Links>
              </Items>
              <Items>
                <Links to="/blog/">ブログ</Links>
              </Items>
              <Items>
                <Links to="/form/inquiry">お問い合わせ</Links>
              </Items>
              <Items>
                <ExternalLinks
                  href="https://www.medley.jp/"
                  target="_blank"
                  rel="noopener"
                >
                  運営会社
                </ExternalLinks>
              </Items>
            </List>
            <AccordionItem>
              <List>
                <AccordionItemHeading onClick={expandIsProducts}>
                  <AccordionItemButton>
                    <Items>
                      <Flex justifyContent="space-between">
                        <Text size="xs" fontWeight="bold" color="white">
                          メドレーのプロダクト
                        </Text>
                        <Arrow expand={isProducts} />
                      </Flex>
                    </Items>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <Items>
                    <ExternalLinks
                      href="https://clinics-cloud.com/"
                      target="_blank"
                      rel="noopener"
                    >
                      クラウド診療支援システム「CLINICS」
                    </ExternalLinks>
                  </Items>
                  <Items>
                    <ExternalLinks
                      href="https://pharms-cloud.com/"
                      target="_blank"
                      rel="noopener"
                    >
                      かかりつけ薬局支援システム「Pharms」
                    </ExternalLinks>
                  </Items>
                  <Items>
                    <ExternalLinks
                      href="https://clinics-app.com/"
                      target="_blank"
                      rel="noopener"
                    >
                      オンライン診療・服薬指導アプリ「CLINICS」
                    </ExternalLinks>
                  </Items>
                  <Items>
                    <ExternalLinks
                      href="https://medley.life/"
                      target="_blank"
                      rel="noopener"
                    >
                      医師たちがつくるオンライン医療事典「MEDLEY」
                    </ExternalLinks>
                  </Items>
                  <Items>
                    <ExternalLinks
                      href="https://job-medley.com/"
                      target="_blank"
                      rel="noopener"
                    >
                      日本最大級の医療介護求人サイト「ジョブメドレー」
                    </ExternalLinks>
                  </Items>
                  <Items>
                    <ExternalLinks
                      href="https://www.kaigonohonne.com/"
                      target="_blank"
                      rel="noopener"
                    >
                      納得できる老人ホーム探し「介護のほんね」
                    </ExternalLinks>
                  </Items>
                  <Items>
                    <ExternalLinks
                      href="https://www.lalu.jp/"
                      target="_blank"
                      rel="noopener"
                    >
                      女性向け生理予測・妊活アプリ「Lalune(ラルーン)」
                    </ExternalLinks>
                  </Items>
                </AccordionItemPanel>
              </List>
            </AccordionItem>
          </OverrideAccordion>
        ) : (
          <>
            <List>
              <Items>
                <Text size="xxs" fontWeight="bold" color="lightgrey">
                  Dentisとは
                </Text>
              </Items>
              <Items>
                <Links to="/features">Dentisの特長</Links>
              </Items>
              <Items>
                <Links to="/benefits">歯科DXの業務</Links>
              </Items>
              <Items>
                <Links to="/function">機能一覧</Links>
              </Items>
              <Items>
                <Links to="/security">セキュリティの取り組み</Links>
              </Items>
              <Items>
                <Links to="/support">充実のサポート</Links>
              </Items>
              <Items>
                <Links to="/vision">Dentisの想い</Links>
              </Items>
            </List>
            <List>
              <Items>
                <Text size="xxs" fontWeight="bold" color="lightgrey">
                  便利な機能
                </Text>
              </Items>
              <Items>
                <Links to="/kakaritsuke">予約システム</Links>
              </Items>
              <Items>
                <Links to="/online">オンライン診療</Links>
              </Items>
              <Items>
                <Links to="/questionnaire">Web問診</Links>
              </Items>
              <Items>
                <Links to="/message">メッセージ配信</Links>
              </Items>
              <Items>
                <Links to="/visit">訪問歯科</Links>
              </Items>
              <Items>
                <Links to="/subkarte">サブカルテ</Links>
              </Items>
              <Items>
                <Links to="/karte">カルテ・レセコン</Links>
              </Items>
              <Items>
                <Links to="/analytics">分析・物販</Links>
              </Items>
            </List>
            <List>
              <Items>
                <Links to="/case">導入事例</Links>
              </Items>
              <Items>
                <Links to="/plan">料金</Links>
              </Items>
              <Items>
                <Links to="/news">お知らせ・イベント</Links>
              </Items>
              <Items>
                <Links to="/blog/">ブログ</Links>
              </Items>
              <Items>
                <Links to="/form/inquiry">お問い合わせ</Links>
              </Items>
              <Items>
                <ExternalLinks
                  href="https://www.medley.jp/"
                  target="_blank"
                  rel="noopener"
                >
                  運営会社
                </ExternalLinks>
              </Items>
            </List>
            <List>
              <Items>
                <Text size="xxs" fontWeight="bold" color="lightgrey">
                  メドレーのプロダクト
                </Text>
              </Items>
              <Items>
                <ExternalLinks
                  href="https://clinics-cloud.com/"
                  target="_blank"
                  rel="noopener"
                >
                  クラウド診療支援システム「CLINICS」
                </ExternalLinks>
              </Items>
              <Items>
                <ExternalLinks
                  href="https://pharms-cloud.com/"
                  target="_blank"
                  rel="noopener"
                >
                  かかりつけ薬局支援システム「Pharms」
                </ExternalLinks>
              </Items>
              <Items>
                <ExternalLinks
                  href="https://clinics-app.com/"
                  target="_blank"
                  rel="noopener"
                >
                  オンライン診療・服薬指導アプリ「CLINICS」
                </ExternalLinks>
              </Items>
              <Items>
                <ExternalLinks
                  href="https://medley.life/"
                  target="_blank"
                  rel="noopener"
                >
                  医師たちがつくるオンライン医療事典「MEDLEY」
                </ExternalLinks>
              </Items>
              <Items>
                <ExternalLinks
                  href="https://job-medley.com/"
                  target="_blank"
                  rel="noopener"
                >
                  日本最大級の医療介護求人サイト「ジョブメドレー」
                </ExternalLinks>
              </Items>
              <Items>
                <ExternalLinks
                  href="https://www.kaigonohonne.com/"
                  target="_blank"
                  rel="noopener"
                >
                  納得できる老人ホーム探し「介護のほんね」
                </ExternalLinks>
              </Items>
              <Items>
                <ExternalLinks
                  href="https://www.lalu.jp/"
                  target="_blank"
                  rel="noopener"
                >
                  女性向け生理予測・妊活アプリ「Lalune(ラルーン)」
                </ExternalLinks>
              </Items>
            </List>
          </>
        )}
      </Wrapper>
      <Copyright>&copy;2022 MEDLEY, INC.</Copyright>
    </Root>
  );
};
